import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOutlinedIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { ToastContainer, toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import Select from "react-select";
import "react-toastify/dist/ReactToastify.css";
import "../campus/Campus.css";
import { hasAdmissionPermission } from "hooks/permissions";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const SportTrialApplications = () => {
  // State variables
  const [campaigns, setCampaigns] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [applicationFormId, setApplicationFormId] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentApplicationId, setCurrentApplicationId] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [formErrors, setFormErrors] = useState({
    campaign: "",
    program: "",
    applicationFormId: "",
  });
  const [filteredApplications, setFilteredApplications] = useState([]);

  // Fetch campaigns
  const getCampaigns = async () => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(
        `${BASE_URL}/campaign/get_campaigns`,
        requestOptions
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCampaigns(data.campaigns || []);
    } catch (error) {
      console.error("Fetch campaigns error:", error);
      toast.error("Failed to fetch campaigns");
    }
  };

  // Fetch degrees/programs in campaign
  const getDegreesInCampaign = async (campaignId) => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(
        `${BASE_URL}/campaign/get_degrees_in_campaign/${campaignId}`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Extract programs from degrees
      const allPrograms = [];
      data.degrees?.forEach((degree) => {
        if (degree.degree_name) {
          allPrograms.push({
            id: degree.degree_id,
            program_name: degree.degree_name,
          });
        }
      });
      setPrograms(allPrograms);
    } catch (error) {
      console.error("Fetch programs error:", error);
      toast.error("Failed to fetch programs");
      setPrograms([]);
    }
  };

  // Fetch sport trial applications
  const getSportTrialApplications = async (campaignId) => {
    if (!campaignId) return;

    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    try {
      const response = await fetch(
        `${BASE_URL}/application/get_sports_trail_applications/${campaignId}`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setApplications(data.applications || []);
      setFilteredApplications(data.applications || []);
    } catch (error) {
      console.error("Fetch applications error:", error);
      toast.error("Failed to fetch applications");
      setApplications([]);
      setFilteredApplications([]);
    }
  };

  // Add sport trial application
  const addApplication = async () => {
    if (!validateForm()) return;

    const applicationData = {
      application_form_id: parseInt(applicationFormId),
      campaign_id: selectedCampaign.value,
      program_id: selectedProgram.value,
    };

    try {
      const response = await fetch(
        `${BASE_URL}/application/add_sports_trail_application`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(applicationData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add application");
      }

      toast.success("Application added successfully");
      getSportTrialApplications(selectedCampaign.value);
      resetForm();
    } catch (error) {
      console.error("Error adding application:", error);
      toast.error(error.message || "Failed to add application");
    } finally {
      setShowAddModal(false);
    }
  };

  // Update sport trial application
  const updateApplication = async () => {
    if (!validateForm()) return;

    const applicationData = {
      id: currentApplicationId,
      application_form_id: parseInt(applicationFormId),
      campaign_id: selectedCampaign.value,
      program_id: selectedProgram.value,
    };

    try {
      const response = await fetch(
        `${BASE_URL}/application/update_sports_trail_application`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(applicationData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update application");
      }

      toast.success("Application updated successfully");
      getSportTrialApplications(selectedCampaign.value);
      resetForm();
    } catch (error) {
      console.error("Error updating application:", error);
      toast.error(error.message || "Failed to update application");
    } finally {
      setShowUpdateModal(false);
    }
  };

  // Delete sport trial application
  const deleteApplication = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/application/delete_sports_trail_application/${currentApplicationId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete application");
      }

      toast.success("Application deleted successfully");
      getSportTrialApplications(selectedCampaign.value);
    } catch (error) {
      console.error("Error deleting application:", error);
      toast.error(error.message || "Failed to delete application");
    } finally {
      setShowDeleteModal(false);
    }
  };

  // View application
  const viewApplication = (id) => {
    const application = applications.find((app) => app.id === id);
    setSelectedApplication(application);
    setShowViewModal(true);
  };

  // Search functionality
  const handleSearchKeywordChange = (e) => {
    const searchTerm = e.target.value.trim().toLowerCase();
    setSearchKeyword(searchTerm);

    if (searchTerm === "") {
      setFilteredApplications(applications);
    } else {
      const filtered = applications.filter((application) => {
        const programMatch =
          application.program_name &&
          application.program_name.toLowerCase().includes(searchTerm);
        const formIdMatch =
          application.application_form_id &&
          application.application_form_id.toString().includes(searchTerm);
        return programMatch || formIdMatch;
      });
      setFilteredApplications(filtered);
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!selectedCampaign) {
      errors.campaign = " * Campaign is required";
    }
    if (!selectedProgram) {
      errors.program = " * Program is required";
    }
    if (!applicationFormId || applicationFormId.trim() === "") {
      errors.applicationFormId = " * Application Form ID is required";
    } else if (isNaN(applicationFormId) || parseInt(applicationFormId) <= 0) {
      errors.applicationFormId = " * Application Form ID must be a positive number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle focus to clear errors
  const handleFocus = (fieldName) => {
    setFormErrors((prevFormErrors) => ({
      ...prevFormErrors,
      [fieldName]: "",
    }));
  };

  // Reset form
  const resetForm = () => {
    setSelectedProgram(null);
    setApplicationFormId("");
    setFormErrors({
      campaign: "",
      program: "",
      applicationFormId: "",
    });
  };

  // Handle campaign selection
  const handleCampaignChange = (selectedOption) => {
    setSelectedCampaign(selectedOption);
    if (selectedOption) {
      getDegreesInCampaign(selectedOption.value);
      getSportTrialApplications(selectedOption.value);
    } else {
      setPrograms([]);
      setApplications([]);
      setFilteredApplications([]);
    }
    setSelectedProgram(null);
    handleFocus("campaign");
  };

  // Dialog handlers
  const showApplicationAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const showApplicationUpdateModal = (id) => {
    const application = applications.find((app) => app.id === id);
    if (application) {
      setCurrentApplicationId(id);
      setSelectedProgram({
        value: application.program_id,
        label: application.program_name,
      });
      setApplicationFormId(application.application_form_id.toString());
      setShowUpdateModal(true);
    }
  };

  const showApplicationDeleteModal = (id) => {
    setCurrentApplicationId(id);
    setShowDeleteModal(true);
  };

  // Initial render
  useEffect(() => {
    getCampaigns();
  }, []);

  // Prepare options for react-select
  const campaignOptions = campaigns.map((campaign) => ({
    value: campaign.id,
    label: campaign.name,
  }));

  const programOptions = programs.map((program) => ({
    value: program.id,
    label: program.program_name,
  }));

  // Table columns
  const columns = [
    { Header: "Sr.No", accessor: "serial", align: "center" },
    { Header: "Application Form ID", accessor: "application_form_id", align: "center" },
    { Header: "Program Name", accessor: "program_name", align: "center" },
    { Header: "Actions", accessor: "actions", align: "center" },
  ];

  // Table rows
  const rows = filteredApplications.map((application, index) => ({
    serial: index + 1,
    application_form_id: application.application_form_id || "N/A",
    program_name: application.program_name || "N/A",
    actions: (
      <>
        {hasAdmissionPermission("SportTrialApplications", "UPDATE") && (
          <IconButton onClick={() => showApplicationUpdateModal(application.id)}>
            <EditIcon className="icon-button-edit" />
          </IconButton>
        )}
        {hasAdmissionPermission("SportTrialApplications", "READ") && (
          <IconButton onClick={() => viewApplication(application.id)}>
            <VisibilityOutlinedIcon className="icon-button-view" />
          </IconButton>
        )}
        {hasAdmissionPermission("SportTrialApplications", "DELETE") && (
          <IconButton onClick={() => showApplicationDeleteModal(application.id)}>
            <DeleteIcon className="icon-button-delete" />
          </IconButton>
        )}
      </>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card className="mt-5 card1">
              <MDBox
                mx={0}
                mt={0}
                py={2}
                px={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "20px" }}
                >
                  <div style={{ minWidth: "300px" }}>
                    <Select
                      options={campaignOptions}
                      value={selectedCampaign}
                      onChange={handleCampaignChange}
                      placeholder="Select Campaign"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: "40px",
                          fontSize: "14px",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>
                  <Paper component="form" className="search-paper">
                    <InputBase
                      sx={{ ml: 1, flex: 1, fontSize: "16px" }}
                      placeholder="Search"
                      inputProps={{ "aria-label": "search applications" }}
                      value={searchKeyword}
                      onChange={handleSearchKeywordChange}
                    />
                    <SearchIcon className="search-icon" />
                  </Paper>
                </div>
                {hasAdmissionPermission("SportTrialApplications", "CREATE") && (
                  <Button
                    variant="filled"
                    endIcon={<AddIcon className="add-icon" />}
                    onClick={showApplicationAddModal}
                    className="add-button"
                  >
                    Add
                  </Button>
                )}
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Application Dialog */}
      <Dialog
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        aria-labelledby="responsive-dialog-title"
        maxWidth="lg"
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-title">
          {"Add Sport Trial Application"}
        </DialogTitle>
        <DialogContent className="dialog-content">
          <div className="row">
            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Campaign</label>
                <Select
                  options={campaignOptions}
                  value={selectedCampaign}
                  onChange={handleCampaignChange}
                  placeholder="Select Campaign"
                  isClearable
                  onFocus={() => handleFocus("campaign")}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.campaign}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Program</label>
                <Select
                  options={programOptions}
                  value={selectedProgram}
                  onChange={(selectedOption) => {
                    setSelectedProgram(selectedOption);
                    handleFocus("program");
                  }}
                  placeholder="Select Program"
                  isClearable
                  onFocus={() => handleFocus("program")}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.program}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Application Form ID</label>
                <input
                  name="application_form_id"
                  type="number"
                  min="1"
                  value={applicationFormId}
                  onChange={(e) => setApplicationFormId(e.target.value)}
                  onFocus={() => handleFocus("applicationFormId")}
                  className="form-control form-control-lg"
                  placeholder="Enter application form ID"
                />
              </div>
              <div className="error-message1">{formErrors.applicationFormId}</div>
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ marginRight: "20px" }}>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowAddModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={addApplication}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Application Dialog */}
      <Dialog
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        aria-labelledby="responsive-dialog-title"
        maxWidth="lg"
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-title">
          {"Update Sport Trial Application"}
        </DialogTitle>
        <DialogContent className="dialog-content">
          <div className="row">
            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Campaign</label>
                <Select
                  options={campaignOptions}
                  value={selectedCampaign}
                  onChange={handleCampaignChange}
                  placeholder="Select Campaign"
                  isClearable
                  onFocus={() => handleFocus("campaign")}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.campaign}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Program</label>
                <Select
                  options={programOptions}
                  value={selectedProgram}
                  onChange={(selectedOption) => {
                    setSelectedProgram(selectedOption);
                    handleFocus("program");
                  }}
                  placeholder="Select Program"
                  isClearable
                  onFocus={() => handleFocus("program")}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "48px",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
              <div className="error-message1">{formErrors.program}</div>
            </div>

            <div className="col-md-6">
              <div className="form-outline mt-3">
                <label>Application Form ID</label>
                <input
                  name="application_form_id"
                  type="number"
                  min="1"
                  value={applicationFormId}
                  onChange={(e) => setApplicationFormId(e.target.value)}
                  onFocus={() => handleFocus("applicationFormId")}
                  className="form-control form-control-lg"
                  placeholder="Enter application form ID"
                />
              </div>
              <div className="error-message1">{formErrors.applicationFormId}</div>
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ marginRight: "20px" }}>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowUpdateModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={updateApplication}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Application Dialog */}
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        PaperProps={{
          sx: {
            width: "450px",
            borderRadius: "20px",
            opacity: 1,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-delete">
          Delete Sport Trial Application
        </DialogTitle>
        <DialogContent className="content-delete">
          Are you sure you want to delete this application?
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={deleteApplication}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Application Dialog */}
      <Dialog
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-delete">
          View Sport Trial Application
        </DialogTitle>
        <DialogContent className="dialog-content">
          {selectedApplication && (
            <>
              <div className="flex-container">
                <strong className="label1">Application Form ID</strong>
                <span className="value1">
                  {selectedApplication.application_form_id || "N/A"}
                </span>
              </div>
              <div className="flex-container">
                <strong className="label1">Campaign</strong>
                <span className="value1">
                  {selectedApplication.campaign_name || "N/A"}
                </span>
              </div>
              <div className="flex-container">
                <strong className="label1">Program Name</strong>
                <span className="value1">
                  {selectedApplication.program_name || "N/A"}
                </span>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowViewModal(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default SportTrialApplications;